import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, CalendarDays, ArrowRight, Car, Zap, Truck, Bike, ShieldCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import PublicNavbar from "../components/landing/PublicNavbar";
import { LandingSections } from "../components/landing/Sections";
import { IMAGES, vehicleTypes, locations } from "../mock";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import { ROLE_HOME } from "../lib/format";
import { DemoLoginButtons } from "./LoginPage";

const TAB_ICONS = { bekas: Car, listrik: Zap, truk: Truck, moge: Bike };

export function LoginDialog({ open, setOpen }) {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const [demoEnabled, setDemoEnabled] = useState(false);
  useEffect(() => { if (open) api.get("/meta").then(({ data }) => setDemoEnabled(!!data.demo_login_enabled)).catch(() => {}); }, [open]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="login-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-extrabold">Masuk ke Inspecar</DialogTitle>
          <DialogDescription>Gunakan akun Google Anda untuk masuk atau mendaftar.</DialogDescription>
        </DialogHeader>
        <Button onClick={() => loginWithGoogle("/masuk")} data-testid="dialog-google-login-btn" className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-sm">
          Lanjutkan dengan Google
        </Button>
        {demoEnabled && (
          <>
            <div className="flex items-center gap-3 my-1"><span className="flex-1 h-px bg-slate-200" /><span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">atau coba demo</span><span className="flex-1 h-px bg-slate-200" /></div>
            <DemoLoginButtons onDone={() => setOpen(false)} />
          </>
        )}
        <button onClick={() => navigate("/masuk")} className="text-xs font-semibold text-blue-600 mx-auto">Buka halaman masuk lengkap →</button>
      </DialogContent>
    </Dialog>
  );
}

function Hero({ onLogin }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [tab, setTab] = useState("bekas");
  const [lokasi, setLokasi] = useState("Jakarta Selatan");
  const [tanggal, setTanggal] = useState("");
  const search = () => {
    if (!user) return onLogin();
    if (user.role !== "pengguna") return navigate(ROLE_HOME[user.role] || "/pilih-peran");
    navigate(`/pengguna/cari?jenis=${tab}&kota=${encodeURIComponent(lokasi)}`);
  };
  return (
    <section id="beranda" className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 lg:pt-16 pb-8 grid lg:grid-cols-2 gap-10 items-center">
        <div className="relative z-10">
          <h1 className="text-4xl sm:text-5xl xl:text-[3.4rem] font-extrabold text-slate-900 leading-[1.08] tracking-tight">
            {t("hero.t1")}<br />{t("hero.t2")} <span className="text-blue-600">{t("hero.accent")}</span><br />{t("hero.t3")}<br />{t("hero.t4")}
          </h1>
          <p className="mt-5 text-slate-500 text-sm sm:text-base max-w-md leading-relaxed">{t("hero.desc")}</p>

          <div className="mt-8 bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 p-4 max-w-xl">
            <div className="flex flex-wrap gap-1 border-b border-slate-100 pb-3">
              {vehicleTypes.map((v) => {
                const Icon = TAB_ICONS[v.id];
                const active = tab === v.id;
                return (
                  <button key={v.id} onClick={() => setTab(v.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold rounded-lg transition-colors ${active ? "text-blue-600 border-b-2 border-blue-600 -mb-[13px] pb-[13px] rounded-none" : "text-slate-500 hover:text-slate-800"}`}>
                    <Icon size={16} /> {t(`vehicle.${v.id}`)}
                  </button>
                );
              })}
            </div>
            <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 pt-4">
              <div>
                <label className="text-[11px] font-semibold text-slate-400 flex items-center gap-1 mb-1"><MapPin size={12} className="text-blue-600" /> {t("hero.lokasi")}</label>
                <Select value={lokasi} onValueChange={setLokasi}>
                  <SelectTrigger className="border-0 border-b border-slate-200 rounded-none shadow-none focus:ring-0 px-0 font-semibold text-slate-800"><SelectValue /></SelectTrigger>
                  <SelectContent>{locations.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-400 flex items-center gap-1 mb-1"><CalendarDays size={12} className="text-slate-500" /> {t("hero.tanggal")}</label>
                <input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)}
                  className="w-full border-0 border-b border-slate-200 bg-transparent text-sm font-semibold text-slate-800 focus:outline-none focus:border-blue-500 py-2" />
              </div>
              <Button onClick={search} data-testid="hero-search-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl px-5 h-11 self-end shadow-lg shadow-blue-600/25">
                {t("hero.cari")} <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl">
            {t("hero.trust").map((b, i) => (
              <div key={i} className="flex items-start gap-2">
                <ShieldCheck size={22} className="text-blue-600 shrink-0 mt-0.5" fill="#dbeafe" />
                <p className="text-[11px] font-bold text-slate-700 leading-tight">{b.title}<br /><span className="font-semibold text-slate-500">{b.sub}</span></p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative lg:h-[560px] h-[340px] rounded-3xl overflow-hidden">
          <img src={IMAGES.hero} alt="Inspector Inspecar memeriksa mobil" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
          <p className="absolute top-8 left-6 font-hand text-3xl sm:text-4xl text-white -rotate-3 leading-tight drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
            {t("hero.hand1")}<br />{t("hero.hand2")}<br />{t("hero.hand3")}
          </p>
          <div className="absolute bottom-6 right-4 sm:right-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3">
            <span className="bg-blue-600 rounded-full p-2"><ShieldCheck size={20} className="text-white" /></span>
            <div>
              <p className="text-sm font-bold text-slate-900">{t("hero.trusted")}</p>
              <p className="text-xs text-amber-500 font-bold mt-0.5">★★★★★ <span className="text-slate-700">4.9/5</span></p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const [loginOpen, setLoginOpen] = useState(false);
  return (
    <div className="min-h-screen bg-white font-sans">
      <PublicNavbar onLogin={() => setLoginOpen(true)} active="beranda" />
      <Hero onLogin={() => setLoginOpen(true)} />
      <LandingSections onCta={() => setLoginOpen(true)} />
      <LoginDialog open={loginOpen} setOpen={setLoginOpen} />
    </div>
  );
}
